import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { UserProfile, CaregiverProfileData } from '../types';
import { logger } from '../utils/logger';
import { api } from '../services/apiClient';
import { isApiEnabled, FEATURE_FLAGS } from '../utils/featureFlags';

const DEFAULT_PROFILES: UserProfile[] = [
  { id: 'mom', name: 'Mom', avatar: '👩', ageRange: 'Adult', isActive: true },
  { id: 'child1', name: 'Child 1', avatar: '👦', ageRange: 'Child', isActive: false },
  { id: 'child2', name: 'Child 2', avatar: '👧', ageRange: 'Child', isActive: false },
  { id: 'grandma', name: 'Grandma', avatar: '👵', ageRange: 'Senior', isActive: false },
];

const DEFAULT_CAREGIVER_PROFILE: CaregiverProfileData = {
  isCaregiver: false,
  preferences: {
    showDailyEncouragement: true,
    enableReminders: true,
    preferredStressLevel: 'medium',
    pickyEaters: false,
    quickMealPrepMax: 20
  },
  reminderSettings: {
    hydration: true,
    nutrition: true,
    selfCare: true,
    customReminders: []
  },
  encouragementHistory: [],
  favoriteMeals: [],
  resetHistory: []
};

const STORAGE_KEY = 'userProfiles';
const CAREGIVER_STORAGE_KEY = 'caregiverProfile';

// --- API response types ---

interface FamilyMemberApi {
  id: string;
  householdId: string;
  name: string;
  avatar: string | null;
  photoUrl: string | null;
  ageRange: string | null;
  dietaryRestrictions: string[];
  allergies: string[];
  preferredFoods: string[];
  dislikedFoods: string[];
  pickyEaterScale: number;
  isActive: boolean;
}

interface HouseholdApiResponse {
  data: {
    id: string;
    userId: string;
    name: string;
    members: FamilyMemberApi[];
  } | null;
}

interface ProfileContextType {
  profiles: UserProfile[];
  activeProfile: UserProfile | null;
  switchProfile: (profileId: string) => void;
  caregiverProfile: CaregiverProfileData;
  updateCaregiverProfile: (updates: Partial<CaregiverProfileData>) => void;
  toggleCaregiverMode: (enabled: boolean) => void;
  isLoadingFromApi: boolean;
  apiError: string | null;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

interface ProfileProviderProps {
  children: ReactNode;
}

// --- API helpers ---

/** Try to load profiles from the API household endpoint. Returns null on failure. */
async function fetchProfilesFromApi(): Promise<UserProfile[] | null> {
  if (!isApiEnabled(FEATURE_FLAGS.profiles)) return null;
  try {
    const res = await api.get<HouseholdApiResponse>('/household');
    if (!res.data || !res.data.members || res.data.members.length === 0) {
      return null;
    }

    // Convert API family members to UserProfile format
    return res.data.members.map((m) => ({
      id: m.id,
      name: m.name,
      avatar: m.avatar || '👤',
      ageRange: m.ageRange || 'Adult',
      isActive: m.isActive,
    }));
  } catch (err) {
    logger.error('Failed to load profiles from API:', err);
    return null;
  }
}

/** Push profiles to API (best-effort, non-blocking). */
function syncProfilesToApi(profiles: UserProfile[]): void {
  if (!isApiEnabled(FEATURE_FLAGS.profiles)) return;
  // We sync the active state of each member. The household API manages members,
  // so we patch each member's isActive status.
  for (const profile of profiles) {
    api.patch(`/household/members/${profile.id}`, {
      isActive: profile.isActive,
    }).catch((err) => {
      logger.error(`Failed to sync profile ${profile.id} to API:`, err);
    });
  }
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const [profiles, setProfiles] = useState<UserProfile[]>(DEFAULT_PROFILES);
  const [caregiverProfile, setCaregiverProfile] = useState<CaregiverProfileData>(DEFAULT_CAREGIVER_PROFILE);
  const [isLoadingFromApi, setIsLoadingFromApi] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Load profiles: try API first, fallback to localStorage
  useEffect(() => {
    let cancelled = false;

    const loadProfiles = async () => {
      setIsLoadingFromApi(true);
      setApiError(null);

      try {
        // Try API first
        const apiProfiles = await fetchProfilesFromApi();
        if (!cancelled && apiProfiles && apiProfiles.length > 0) {
          setProfiles(apiProfiles);
          // Update localStorage cache
          localStorage.setItem(STORAGE_KEY, JSON.stringify(apiProfiles));
          setIsLoadingFromApi(false);
          return;
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'API load failed';
          setApiError(message);
          logger.error('API profile load failed, falling back to localStorage:', err);
        }
      }

      // Fall back to localStorage
      if (!cancelled) {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          try {
            const parsedProfiles = JSON.parse(stored);
            setProfiles(parsedProfiles);
          } catch (error) {
            logger.error('Failed to parse stored profiles:', error);
          }
        }
        setIsLoadingFromApi(false);
      }
    };

    loadProfiles();

    // Load caregiver profile (localStorage only, no API equivalent yet)
    const caregiverStored = localStorage.getItem(CAREGIVER_STORAGE_KEY);
    if (caregiverStored) {
      try {
        const parsedCaregiver = JSON.parse(caregiverStored);
        setCaregiverProfile(parsedCaregiver);
      } catch (error) {
        logger.error('Failed to parse stored caregiver profile:', error);
      }
    }

    return () => { cancelled = true; };
  }, []);

  // Debounced save: profiles to localStorage + API (300ms)
  const profileSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (profileSaveTimer.current) clearTimeout(profileSaveTimer.current);
    profileSaveTimer.current = setTimeout(() => {
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
      // Dual-write: also push to API (non-blocking)
      syncProfilesToApi(profiles);
    }, 300);
    return () => {
      if (profileSaveTimer.current) clearTimeout(profileSaveTimer.current);
    };
  }, [profiles]);

  // Debounced save: caregiver profile to localStorage (300ms)
  const caregiverSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialCaregiverMount = useRef(true);
  useEffect(() => {
    if (isInitialCaregiverMount.current) {
      isInitialCaregiverMount.current = false;
      return;
    }
    if (caregiverSaveTimer.current) clearTimeout(caregiverSaveTimer.current);
    caregiverSaveTimer.current = setTimeout(() => {
      localStorage.setItem(CAREGIVER_STORAGE_KEY, JSON.stringify(caregiverProfile));
    }, 300);
    return () => {
      if (caregiverSaveTimer.current) clearTimeout(caregiverSaveTimer.current);
    };
  }, [caregiverProfile]);

  const activeProfile = useMemo(() => profiles.find(p => p.isActive) || null, [profiles]);

  const switchProfile = useCallback((profileId: string) => {
    setProfiles(prev => prev.map(p => ({
      ...p,
      isActive: p.id === profileId
    })));
  }, []);

  const updateCaregiverProfile = useCallback((updates: Partial<CaregiverProfileData>) => {
    setCaregiverProfile(prev => ({
      ...prev,
      ...updates,
      preferences: {
        ...prev.preferences,
        ...(updates.preferences || {})
      },
      reminderSettings: {
        ...prev.reminderSettings,
        ...(updates.reminderSettings || {})
      }
    }));
  }, []);

  const toggleCaregiverMode = useCallback((enabled: boolean) => {
    setCaregiverProfile(prev => ({
      ...prev,
      isCaregiver: enabled
    }));
  }, []);

  const value: ProfileContextType = useMemo(() => ({
    profiles,
    activeProfile,
    switchProfile,
    caregiverProfile,
    updateCaregiverProfile,
    toggleCaregiverMode,
    isLoadingFromApi,
    apiError,
  }), [profiles, activeProfile, switchProfile, caregiverProfile, updateCaregiverProfile, toggleCaregiverMode, isLoadingFromApi, apiError]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};
